import React from 'react';
import { useEffect, useState } from "react";
import { LANGTYPE_EN, useLanguage } from '@/app/utils/LanguageContext';
import { useMyContext } from '@/app/utils/MyContext';

import { text_size } from "@/app/utils/MixinSize";
import styled from "styled-components";

import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";

import Disclaimer from "@/app/ui/Disclaimer";
import { generateHorseProfileUrl, getSearchParams, generateUrl, setRacesDetailLoading, parseBetline, adobeData, generateAdobeDataRaceNo } from '@/app/utils/Common';
import ExpertProfile from "@/app/ui/Common/ExpertProfile";
import { getExpertColumn, getHorseByMeetingRace, getTrackAnalysisExperts, getMeetingPmPoolStatus } from "@/app/utils/request/requestList";
import { formattedDate } from "@/app/utils/DayJS";
import NoInformation from '@/app/ui/Common/NoInformation';
import ExpertColumnPC from "@/app/ui/RaceDetail/components/RaceCard/ExpertColumn/pc";
import ExpertColumnM from "@/app/ui/RaceDetail/components/RaceCard/ExpertColumn/mobile";
import betslip from "@/app/ui/RaceDetail/components/RaceCard/ExpertColumn/mobile/betslip.json";  

const DivContainer =  styled('div')<{ $assetPrefix?: string }>(({ $assetPrefix }) => `
    border-radius: 0 0 4px 4px;
    background: #fff;
    background-repeat: repeat-y;
`);

const DivContainerM = styled.div`
    background-color: #FFFFFF;
    border-radius: 0 0 4px 4px;
    padding:12px;
    > div + div {
        margin-top: 20px;
    }
`;

const TopContentWrapper = styled.div`
    max-width: 1056px;
    display: flex;
    gap: 32px;
    padding: 20px 12px 16px 12px;
`;

const LeftColumnWrapper = styled.div`
    max-width: 628px;
    > div + div {
        margin-top: 24px;
    }
`;

const RightColumnWrapper = styled.div`
    width: 372px;
    min-width: 372px;
`;

const BottomContentWrapper = styled.div`
    max-width: 1056px; 
    padding: 16px 12px 20px 12px;
`;

const ExpertColumnHeader = styled.div`
  width: 100%;
  height: 36px;
  padding-left: 12px;
  ${text_size(15)}
  line-height: 36px;
  font-weight: 500;
  background-color: #F4F4F4;
`;

const DivCommentaryContent = styled.div`
    min-height: 380px;
`;

const DivTitle = styled.div`
    ${text_size(15)}
    font-weight: 700;
    line-height: 17px;
    margin-bottom: 8px;
`;

const DivContent = styled.div`
    ${text_size(13)}
    font-weight: 500;
    > p + p {
        margin-top: 12px;
    }
`;

const DivTopSolid = styled.div`
    border-top: 1px solid #B4B4B4;
    clear: both;
    margin: 0 12px;
    max-width: 1032px;
`;

/* Selections Column */
const SelectionWrapper = styled('div')(() => ({
    boxShadow: "0px 2px 10px 0px #0000001A"
}));

const Header = styled.div`
    padding: 10px 12px 10px 12px;
    width: 100%;
    height: 37px;
    border-radius: 4px 4px 0px 0px;
    background: var(--extraPalette-primary-darkGrey);
    text-align: center;
    ${text_size(13)}
    font-weight: 500;
    line-height: 17px;
    color: white;
`;

const HorseListRow = styled.div`
    display: flex;
    gap: 12px;
    height: 36px;
    padding: 8px 12px;
    color: var(--extraPalette-text-primary);
    ${text_size(13)}
    font-weight: 500;
    align-items: center;

    p{
        line-height: 17px;
    }

    &:nth-of-type(odd) {
        background-color: #F4F4F4
    }
    &:nth-of-type(even) {
        background-color: #FFFFFF
    }

`;

const BetWrapper = styled.div`
    border-top: 1px solid #B4B4B4;
    padding: 12px;

    > div + div{
        margin-top: 20px;
    }
`;

const BetItemRow = styled.div`
    display: flex;
    gap: 12px;
    color: var(--extraPalette-text-primary);
    ${text_size(13)}
    font-weight: 500;
    align-items: start;

    p{
        line-height: 17px;
    }
`;

const BetItemTable = styled.div`
    width: 100%;
    > div + div {
        margin-top: 8px;
    }
    > div :first-of-type {
        margin-top: 4px;
    }
`;

const BetRaceRow = styled.div`
    width: 100%; 
    color: #333333;
    line-height: 17px;
    ${text_size(13)}
    font-weight: 500;
    align-items: start;

    > div {
        display: flex;
        flex-wrap: nowrap;
        gap: 15px;
    } 
    .betRaceRowBold {
        font-weight: 700;
        margin-top: 4px;
    }
    .betRaceRowFirst {
        margin-top: 8px;
    }
    > div :first-of-type {
        margin-top: 0;
    }
    .betRaceColFirst {
        white-space: nowrap;
        min-width: 30px;
    }
    @media only screen and (min-width: 1680px) {
        line-height: 20px;
        .betRaceColFirst {min-width: 35px;}
    }
    
`;
interface propType {
    setRacesDetailLoading: (loading: boolean) => void;
    type?: string;
}

const ExpertColumn: React.FC<propType> = (props: propType) => {
    const { state: lang } = useLanguage();
    const { t } = lang;
    const { state: myContextState, dispatch: myContextDispatch } = useMyContext();
    const [expertName, setExpertName] = useState<string>('');

    const [horseList, setHorseList] = useState<any>([]);
    const [betTypeList, setBetTypeList] = useState<any>([]);
    const [expertHorseData, setExpertHorseData] = useState<any>([]);
    const [commentary, setCommentary] = useState<any>(null);
    const { b_cid } = getSearchParams(new URLSearchParams(window.location.search));
    const [trackAnalysisExperts, setTrackAnalysisExperts] = useState<any>(null);
    const [expertNameEn, setExpertNameEn] = useState<string>();
    const [pmPools, setPmPools] = useState<any>(null);

    useEffect(() => {
        getExpertColumn(
            lang.langCode,
            myContextState.season,
            myContextState.selectedMeeting?.displayName,
            myContextState.selectedRace?.displayName
        )
            .then((res: any) => {
                const data = res?.item;
                setCommentary(data?.commentary?.value);
                const hlist = [];
                const betlist = [];
                setExpertName(data?.expert.value || '')
                 if (data?.expert.value) {
                    getTrackAnalysisExperts(lang.langCode, data?.expert.value || '')
                    .then((resExperts: any) => {
                        setTrackAnalysisExperts(resExperts?.item);
                        setExpertNameEn(resExperts?.enItem?.expertName?.value || "");  
                    })
                    .catch((e: any) => {
                        console.log(e);
                    });
                }
                for (let index = 1; index <= 10; index++) {
                    const num = index != 10 ? "0" + index : index;
                    const horseName = data[`horseName${num}`]?.value;
                    const horseNumber = data[`horseNumber${num}`]?.value;

                    if (horseName && horseName != "") {
                        hlist.push({
                            horseName: horseName,
                            horseNumber: horseNumber
                        });
                    }

                    const betSelectionName = data[`betSelectionName${num}`]?.value;
                    const selectionLinkParameters = data[`selectionLinkParameters${num}`]?.value;
                    const betSelectionDetails = data[`betSelectionDetails${num}`]?.value;
                    const betslipLink = data[`betslipLink${num}`]?.rendered || "" as string;
                    if (betSelectionName && betSelectionName != "" && betSelectionDetails) {
                        const betL = betSelectionDetails?.split("\r\n")?.map((r: string) => r.split("\t"));
                        
                        // let _betslipLink = betslipLink || '';
                        // if (betslipLink) {
                        //     const r_link = new URLSearchParams(new URL(betslipLink).search).get('r');
                        //     if (r_link) {
                        //         _betslipLink = _betslipLink + encodeURIComponent(`?b_cid=SPLDSP_racing/simulcast-ExpertColumn_AddSlip`)
                        //     } 
                        // }
                        const b = {
                            title: betSelectionName,
                            link: selectionLinkParameters ? generateUrl(selectionLinkParameters, { "b_cid": "SPLDSP_racing/simulcast-ExpertColumn_BetType" }, false, true) : "",
                            betList: parseBetline(betSelectionName, betSelectionDetails),
                            betslipLink: betslipLink, 
                        }
                        console.log("betSelectionName,  betSelectionDetails----",betSelectionName,  betSelectionDetails)
                        console.log("parseBetline()----",parseBetline(betSelectionName,  betSelectionDetails))
                        betlist.push(b);
                    }
                    
                }
                setExpertHorseData(hlist);
                setBetTypeList(betlist);
            })
            .catch((e: any) => {
                console.log(e);
            }).finally(() => {
                setRacesDetailLoading(props.setRacesDetailLoading)
            });

        getMeetingPmPoolStatus(
            formattedDate(myContextState.selectedMeetingDate, "YYYY-MM-DD"),
            myContextState.selectedMeetingVenueCode,
            ["DT", "DBL", "TBL"]
        )
        .then((res: any) => {
            setPmPools(res?.raceMeetingProfile?.[0])
        })
        .catch((e: any) => {
            console.log(e);
        }).finally(() => {
            setRacesDetailLoading(props.setRacesDetailLoading)
        });
    }, [myContextState.selectedMeeting, myContextState.selectedRace?.displayName]);

    useEffect(() => {
        getHorseByMeetingRace(
            formattedDate(myContextState.selectedMeetingDate, "YYYYMMDD"),
            myContextState.selectedMeetingVenueCode,
            myContextState.selectedRaceNo.valueOf().toString(),
            []
        )
            .then((res: any) => {
                const horseData = res?.simulcastHorse;
                const newD = expertHorseData?.map((item: any) => {
                    const info = horseData?.find((h: any) => h[lang.langType === LANGTYPE_EN ? "name_en" : "name_ch"].toLowerCase().includes(item.horseName.toLowerCase()));

                    return {
                        horseName: item.horseName,
                        horseNameEn: info?.name_en || item.horseName,  // for WA english name
                        horseNumber: item.horseNumber,
                        link: info?.brandNumber ? generateHorseProfileUrl(
                            lang.langCode,
                            myContextState.selectedMeetingDate,
                            myContextState.selectedMeetingVenueCode,
                            myContextState.selectedRaceNo.valueOf().toString(),
                            info?.brandNumber,
                            item.horseNumber,
                            "ExpertColumn") : ''
                    }
                });
                setHorseList(newD);
            })
            .catch((e: any) => {
                console.log(e);
            }).finally(() => {
                setRacesDetailLoading(props.setRacesDetailLoading)
            });
    }, [expertHorseData]);

    useEffect(() => {
        if(expertNameEn) { 
            adobeData(lang.langType === LANGTYPE_EN ? "en" : "zh-hk", "pageview", {
                pageId: "racing/simulcast",
                raceNo: generateAdobeDataRaceNo(myContextState.selectedMeetingDate, myContextState.selectedMeetingVenueCode, myContextState.selectedRaceNo.valueOf()),
                internalAd: b_cid,
                country: myContextState.selectedMeeting?.countryNameEn || '',
                author: expertNameEn
            });  
        }
    }, [expertNameEn])

    console.log(betTypeList)

    return (
        <>
            {props.type === "pc" ?
                (
                    <DivContainer $assetPrefix={myContextState.assetPrefix}>
                        <ExpertColumnHeader id="ExpertColumn_Header">
                            {t('expert_column')}
                        </ExpertColumnHeader>

                        {
                            commentary ?
                                <ExpertColumnPC
                                    betTypeList={betTypeList}
                                    commentary={commentary}
                                    horseList={horseList}
                                    trackAnalysisExperts={trackAnalysisExperts}
                                    expertName={expertName}
                                    expertNameEn={expertNameEn}
                                    menuTab="refInfo"
                                    pmPoolsData={pmPools}
                                /> : <NoInformation />
                        }

                    </DivContainer >
                ) : (
                    commentary ?
                        <ExpertColumnM 
                            betTypeList={betTypeList}
                            commentary={commentary}
                            horseList={horseList}
                            trackAnalysisExperts={trackAnalysisExperts}
                            expertName={expertName}
                            expertNameEn={expertNameEn}
                            menuTab="refInfo"
                            pmPoolsData={pmPools}
                        /> 
                        : <NoInformation />
                )
            }
        </>
    )
}

export default ExpertColumn;